// LoginForm.tsx — the sign-in card, rendered on the app's own origin.
//
// Pairs with lib/oidcrp's PasswordLogin: the form posts credentials to the
// app's own backend, which relays them to authz under the OAuth password
// grant and sets the session cookie. The user never sees an authz-domain
// page, and the SPA never touches a token — the password goes straight into
// a fetch body and the session comes back as an httpOnly cookie.
//
// Social sign-in stays a redirect, because Google and GitHub own those
// consent screens and always will. Those buttons carry `idp_hint`, which
// authz's /authorize forwards, so they skip authz's own login page and land
// on the provider directly.
//
// Layout follows the authz Log In card (DESIGN.md's canonical reference):
// band-free Card, large title doing the header's job, dashed `Text` inputs,
// semantic button variants, outlined Alert for errors.
import { useState } from 'react';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { BsBoxArrowInRight, BsGithub, BsGoogle, BsShieldLock } from 'react-icons/bs';
import Text from './ui/Text';

export interface LoginFormProps {
    /** Card title. Default "Log In". */
    title?: string;
    /** Where to land after a successful sign-in. Default "/". */
    returnTo?: string;
    /** Backend route that accepts the credentials. Default "/api/oidc/password-login". */
    passwordLoginPath?: string;
    /** Backend route that starts the redirect flow. Default "/api/oidc/login". */
    loginPath?: string;
    /** Query-parameter name the backend reads for the return path. Default "return_to". */
    returnParam?: string;
    /**
     * Social providers to offer as one-click buttons. Empty renders none.
     * Values are `idp_hint`s understood by authz — "google", "github".
     */
    providers?: string[];
    /**
     * When false the password fields are hidden and only the redirect options
     * remain. Lets one component serve a deployment that hasn't enabled
     * embedded login without the caller branching on it.
     */
    password?: boolean;
    /** Issuer shown on the single-sign-on fallback. Default "authz.lab0.us". */
    issuerLabel?: string;
    /**
     * Called instead of navigating when sign-in succeeds. Apps that keep
     * their auth state in React (rather than reloading) use this to refresh
     * their `useAuth` and route onward.
     */
    onSuccess?: (returnTo: string) => void;
}

// PROVIDER_CHROME maps an idp_hint to its button treatment. Variants are
// semantic per DESIGN.md C-BTN-1 — GitHub's black is Bootstrap's `dark`,
// Google's red is `danger` — so no app needs bespoke CSS to render these.
const PROVIDER_CHROME: Record<string, { label: string; variant: string; Icon: typeof BsGithub }> = {
    google: { label: 'Log in with Google', variant: 'danger', Icon: BsGoogle },
    github: { label: 'Log in with GitHub', variant: 'dark', Icon: BsGithub },
};

export function LoginForm(props: LoginFormProps) {
    const {
        title = 'Log In',
        returnTo = '/',
        passwordLoginPath = '/api/oidc/password-login',
        loginPath = '/api/oidc/login',
        returnParam = 'return_to',
        providers = ['google', 'github'],
        password: passwordEnabled = true,
        issuerLabel = 'authz.lab0.us',
        onSuccess,
    } = props;

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const safeReturnTo = isSafeReturnPath(returnTo) ? returnTo : '/';
    const redirectUrl = (hint?: string) => {
        const q = new URLSearchParams({ [returnParam]: safeReturnTo });
        if (hint) q.set('idp_hint', hint);
        return `${loginPath}?${q.toString()}`;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(passwordLoginPath, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // The session arrives as a Set-Cookie on this response, so the
                // request has to be credentialed even though we send no cookie.
                credentials: 'include',
                body: JSON.stringify({ email, password, return_to: safeReturnTo }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.message || data.error || `Sign-in failed (${res.status})`);
            }
            const next = typeof data.return_to === 'string' && isSafeReturnPath(data.return_to)
                ? data.return_to
                : safeReturnTo;
            // Clear the password from state before we hand off, so it isn't
            // sitting in a live component if navigation is slow or intercepted.
            setPassword('');
            if (onSuccess) {
                onSuccess(next);
            } else {
                // Full navigation rather than a router push: the session cookie
                // is new, and every /api probe the app made while anonymous
                // should be re-run against it.
                window.location.assign(next);
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.');
            setLoading(false);
        }
    };

    const providerButtons = providers
        .map((p) => ({ id: p, chrome: PROVIDER_CHROME[p] }))
        .filter((p) => p.chrome);

    return (
        <div className="auth-wrapper">
            <Card style={{ width: '420px', maxWidth: '100%' }}>
                <Card.Body>
                    <h2 className="text-center mb-4">{title}</h2>
                    {error && <Alert variant="danger">{error}</Alert>}

                    {providerButtons.length > 0 && (
                        <div className="d-flex flex-column gap-2 mb-3">
                            {providerButtons.map(({ id, chrome }) => (
                                <Button
                                    key={id}
                                    variant={chrome.variant}
                                    onClick={() => { window.location.href = redirectUrl(id); }}
                                    className="d-flex align-items-center justify-content-center gap-2"
                                >
                                    <chrome.Icon /> {chrome.label}
                                </Button>
                            ))}
                        </div>
                    )}

                    {passwordEnabled ? (
                        <>
                            {providerButtons.length > 0 && (
                                <div className="text-center text-muted my-3">OR</div>
                            )}
                            <Form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
                                <Form.Group controlId="login-email">
                                    <Form.Label>Email</Form.Label>
                                    <Text
                                        type="email"
                                        style={{ width: '100%' }}
                                        required
                                        value={email}
                                        placeholder="Enter email"
                                        onChange={(e) => setEmail(e.target.value)}
                                        autoComplete="email"
                                    />
                                </Form.Group>
                                <Form.Group controlId="login-password">
                                    <Form.Label>Password</Form.Label>
                                    <Text
                                        type="password"
                                        style={{ width: '100%' }}
                                        required
                                        value={password}
                                        placeholder="Password"
                                        onChange={(e) => setPassword(e.target.value)}
                                        autoComplete="current-password"
                                    />
                                </Form.Group>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={loading}
                                    className="d-flex align-items-center justify-content-center gap-2"
                                >
                                    <BsBoxArrowInRight /> {loading ? 'Logging in…' : 'Log In'}
                                </Button>
                            </Form>
                        </>
                    ) : (
                        // No password path here, so the redirect is the primary
                        // action rather than a fallback link.
                        <Button
                            variant="primary"
                            onClick={() => { window.location.href = redirectUrl(); }}
                            className="d-flex align-items-center justify-content-center gap-2 w-100"
                        >
                            <BsBoxArrowInRight /> Log in with {issuerLabel}
                        </Button>
                    )}

                    {passwordEnabled && (
                        // Tertiary, because it's the path for someone who already
                        // has a session at the issuer and would rather not retype
                        // anything — not the one we expect most people to take.
                        <div className="text-center mt-3 text-muted">
                            <Button
                                variant="link"
                                className="p-0 align-baseline d-inline-flex align-items-center gap-1"
                                onClick={() => { window.location.href = redirectUrl(); }}
                            >
                                <BsShieldLock /> Use single sign-on
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}

// isSafeReturnPath keeps the post-login destination on this origin. Anything
// absolute, protocol-relative, or not rooted is an open-redirect vector.
function isSafeReturnPath(p: string | undefined): boolean {
    if (!p) return false;
    if (!p.startsWith('/')) return false;
    if (p.startsWith('//')) return false;
    return true;
}

export default LoginForm;
